import { useState, useEffect, useCallback } from "react";
import { adminApi, publicApi } from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "../components/ui/pagination";
import { formatCurrency, truncate } from "../lib/utils";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Image,
  Loader2,
  X,
  Upload,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const pageSize = 20; // 20 products per page

  // Settings data
  const [origins, setOrigins] = useState([]);
  const [weaveTypes, setWeaveTypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [fabrics, setFabrics] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [tags, setTags] = useState([]);

  // Form state
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    discount_percentage: "",
    origin_id: "",
    weave_type_id: "",
    fabric_id: "",
    occasion_ids: [],
    tag_ids: [],
    color_variants: [],
    is_active: true,
    meta_title: "",
    meta_description: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getProducts({
        search: search || undefined,
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
      });
      setProducts(res.data);
      // Estimate total - if we got pageSize items, there might be more
      if (res.data.length === pageSize) {
        setTotalProducts(currentPage * pageSize + 1); // At least one more page
      } else {
        setTotalProducts((currentPage - 1) * pageSize + res.data.length);
      }
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [currentPage, search]);

  const fetchSettings = async () => {
    try {
      const [originsRes, colorsRes, fabricsRes, occasionsRes, tagsRes] =
        await Promise.all([
          publicApi.getOrigins(),
          publicApi.getColors(),
          publicApi.getFabrics(),
          publicApi.getOccasions(),
          publicApi.getTags(),
        ]);
      setOrigins(originsRes.data);
      setColors(colorsRes.data);
      setFabrics(fabricsRes.data);
      setOccasions(occasionsRes.data);
      setTags(tagsRes.data);
    } catch (error) {
      console.error("Failed to load settings");
    }
  };

  const fetchWeaveTypes = async (originId) => {
    if (!originId) {
      setWeaveTypes([]);
      return;
    }
    try {
      const res = await publicApi.getWeaveTypes(originId);
      setWeaveTypes(res.data);
    } catch (error) {
      console.error("Failed to load weave types");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchData();
  };

  const openNewProduct = () => {
    setEditingProduct(null);
    setForm({
      name: "",
      description: "",
      price: "",
      discount_percentage: "",
      origin_id: "",
      weave_type_id: "",
      fabric_id: "",
      occasion_ids: [],
      tag_ids: [],
      color_variants: [],
      is_active: true,
      meta_title: "",
      meta_description: "",
    });
    setWeaveTypes([]);
    setShowModal(true);
  };

  const openEditProduct = async (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      discount_percentage: (product.discount_percentage || 0).toString(),
      origin_id: product.origin_id,
      weave_type_id: product.weave_type_id,
      fabric_id: product.fabric_id,
      occasion_ids: product.occasion_ids || [],
      tag_ids: product.tag_ids || [],
      color_variants: product.color_variants || [],
      is_active: product.is_active,
      meta_title: product.meta_title || "",
      meta_description: product.meta_description || "",
    });
    await fetchWeaveTypes(product.origin_id);
    setShowModal(true);
  };

  const handleOriginChange = (originId) => {
    setForm({ ...form, origin_id: originId, weave_type_id: "" });
    fetchWeaveTypes(originId);
  };

  const addColorVariant = () => {
    setForm({
      ...form,
      color_variants: [
        ...form.color_variants,
        { color_id: "", images: [], stock: 0, sku: "" },
      ],
    });
  };

  const updateColorVariant = (index, field, value) => {
    const variants = [...form.color_variants];
    variants[index][field] = value;
    setForm({ ...form, color_variants: variants });
  };

  const removeColorVariant = (index) => {
    const variants = form.color_variants.filter((_, i) => i !== index);
    setForm({ ...form, color_variants: variants });
  };

  console.log("editing product", editingProduct);
  console.log("form state", form);
  // Generic asset upload for new products (no productId needed)
  const handleImageUpload = async (productInfo, variantIndex, file) => {
    const formData = new FormData();
    console.log("Uploading file", file);
    formData.append("file", file);
    formData.append("entity_type", "product-variant");
    const colorId =
      productInfo?.color_variants?.[variantIndex]?.color_id || "-1";
    formData.append("product_slug", productInfo?.slug || "unattached"); // Use "unattached" for new products
    formData.append("color_id", colorId); // Pass color variant index for proper association
    // entity_id is omitted for unattached upload
    try {
      const res = await adminApi.uploadAsset(formData); // expects { url: ... }
      toast.success("Image uploaded successfully");
      return res.data.url;
    } catch (error) {
      let message = "Failed to upload image";
      if (error.response && error.response.data && error.response.data.detail) {
        message += `: ${error.response.data.detail}`;
      } else if (error.message) {
        message += `: ${error.message}`;
      }
      console.error("Asset upload error", error);
      toast.error(message);
      return null;
    }
  };

  const handleSave = async () => {
    if (
      !form.name ||
      !form.price ||
      !form.origin_id ||
      !form.weave_type_id ||
      !form.fabric_id
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        discount_percentage: form.discount_percentage ? parseFloat(form.discount_percentage) : 0,
        color_variants: form.color_variants.map((v) => ({
          ...v,
          stock: parseInt(v.stock) || 0,
        })),
      };

      if (editingProduct) {
        await adminApi.updateProduct(editingProduct.id, data);
        toast.success("Product updated successfully");
      } else {
        await adminApi.createProduct(data);
        toast.success("Product created successfully");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await adminApi.deleteProduct(product.id);
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const toggleOccasion = (occasionId) => {
    const current = form.occasion_ids || [];
    if (current.includes(occasionId)) {
      setForm({
        ...form,
        occasion_ids: current.filter((id) => id !== occasionId),
      });
    } else {
      setForm({ ...form, occasion_ids: [...current, occasionId] });
    }
  };

  const toggleTag = (tagId) => {
    const current = form.tag_ids || [];
    if (current.includes(tagId)) {
      setForm({ ...form, tag_ids: current.filter((id) => id !== tagId) });
    } else {
      setForm({ ...form, tag_ids: [...current, tagId] });
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-primary font-semibold">
            Products
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your saree collection
          </p>
        </div>
        <Button
          onClick={openNewProduct}
          className="bg-primary text-white hover:bg-primary/90 active:scale-95"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Search */}
      <Card className="card-base">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 input-base"
                data-testid="search-input"
              />
            </div>
            <Button
              type="submit"
              variant="outline"
              className="border-primary text-primary"
            >
              Search
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead className="w-20">Image</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Origin</TableHead>
                  <TableHead>Weave</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No products found. Create your first product!
                    </TableCell>
                  </TableRow>
                ) : (
                  products.map((product) => (
                    <TableRow key={product.id} className="hover:bg-canvas/50">
                      <TableCell>
                        {product.color_variants?.[0]?.images?.[0] ? (
                          <img
                            src={`${product.color_variants[0].images[0]}`}
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded-md"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-md flex items-center justify-center">
                            <Image className="w-5 h-5 text-muted-foreground" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-primary">
                        {truncate(product.name, 30)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {product.origin_name}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {product.weave_type_name}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(product.price)}
                        {product.discount_percentage > 0 && (
                          <span className="block text-xs text-green-600">
                            {product.discount_percentage}% off
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            product.total_stock < 5
                              ? "badge-error"
                              : "badge-success"
                          }
                        >
                          {product.total_stock}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            product.is_active
                              ? "badge-success"
                              : "badge-pending"
                          }
                        >
                          {product.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditProduct(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(product)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && products.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalProducts)} of ~{totalProducts}{" "}
            products
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>

              {/* First page */}
              {currentPage > 3 && (
                <PaginationItem>
                  <PaginationLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentPage(1);
                    }}
                  >
                    1
                  </PaginationLink>
                </PaginationItem>
              )}

              {/* Ellipsis before current page group */}
              {currentPage > 4 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Pages around current page */}
              {Array.from({ length: Math.min(5, Math.ceil(totalProducts / pageSize)) }, (_, i) => {
                let pageNum;
                if (Math.ceil(totalProducts / pageSize) <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= Math.ceil(totalProducts / pageSize) - 2) {
                  pageNum = Math.ceil(totalProducts / pageSize) - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                if (pageNum <= 0 || pageNum > Math.ceil(totalProducts / pageSize)) return null;

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      href="#"
                      isActive={pageNum === currentPage}
                      onClick={(e) => {
                        e.preventDefault();
                        setCurrentPage(pageNum);
                      }}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {/* Ellipsis after current page group */}
              {currentPage < Math.ceil(totalProducts / pageSize) - 3 && (
                <PaginationItem>
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (products.length === pageSize) setCurrentPage(currentPage + 1);
                  }}
                  className={products.length < pageSize ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Product Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-primary">
              {editingProduct ? "Edit Product" : "New Product"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="text-primary font-medium">
                  Product Name *
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Sambalpuri Silk Saree"
                  className="input-base mt-1"
                  data-testid="product-name-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-primary font-medium">Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  placeholder="Beautiful handwoven saree..."
                  className="input-base mt-1"
                  rows={3}
                />
              </div>
              <div className="col-span-2">
                <Label className="text-primary font-medium">Price (₹) *</Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="5000"
                  className="input-base mt-1"
                  data-testid="product-price-input"
                />
              </div>
              <div className="col-span-1">
                <Label className="text-primary font-medium">Discount (%)</Label>
                <Select
                  value={form.discount_percentage || ""}
                  onValueChange={(value) => setForm({ ...form, discount_percentage: value })}
                >
                  <SelectTrigger className="input-base mt-1">
                    <SelectValue placeholder="Select discount" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(100)].map((_, i) => (
                      <SelectItem key={i} value={i.toString()}>
                        {i === 0 ? "No discount" : `${i}% off`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.discount_percentage > 0 && (
                  <p className="text-xs text-green-600 mt-1">
                    Final price: ₹{Math.round(parseFloat(form.price || 0) * (1 - parseFloat(form.discount_percentage) / 100)).toLocaleString("en-IN")}
                  </p>
                )}
              </div>
            </div>
            {/* Categories */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-primary font-medium">Origin *</Label>
                <Select
                  value={form.origin_id}
                  onValueChange={handleOriginChange}
                >
                  <SelectTrigger
                    className="input-base mt-1"
                    data-testid="origin-select"
                  >
                    <SelectValue placeholder="Select origin" />
                  </SelectTrigger>
                  <SelectContent>
                    {origins.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-primary font-medium">Weave Type *</Label>
                <Select
                  value={form.weave_type_id}
                  onValueChange={(v) => setForm({ ...form, weave_type_id: v })}
                  disabled={!form.origin_id}
                >
                  <SelectTrigger
                    className="input-base mt-1"
                    data-testid="weave-type-select"
                  >
                    <SelectValue placeholder="Select weave" />
                  </SelectTrigger>
                  <SelectContent>
                    {weaveTypes.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-primary font-medium">Fabric *</Label>
                <Select
                  value={form.fabric_id}
                  onValueChange={(v) => setForm({ ...form, fabric_id: v })}
                >
                  <SelectTrigger
                    className="input-base mt-1"
                    data-testid="fabric-select"
                  >
                    <SelectValue placeholder="Select fabric" />
                  </SelectTrigger>
                  <SelectContent>
                    {fabrics.map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Occasions */}
            <div>
              <Label className="text-primary font-medium mb-2 block">
                Occasions
              </Label>
              <div className="flex flex-wrap gap-2">
                {occasions.map((o) => (
                  <Badge
                    key={o.id}
                    variant={
                      form.occasion_ids?.includes(o.id) ? "default" : "outline"
                    }
                    className={`cursor-pointer transition-all ${
                      form.occasion_ids?.includes(o.id)
                        ? "bg-primary text-white"
                        : "hover:bg-primary/10"
                    }`}
                    onClick={() => toggleOccasion(o.id)}
                  >
                    {o.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <Label className="text-primary font-medium mb-2 block">
                Tags
              </Label>
              <div className="flex flex-wrap gap-2">
                {tags.map((t) => (
                  <Badge
                    key={t.id}
                    variant={
                      form.tag_ids?.includes(t.id) ? "default" : "outline"
                    }
                    className="cursor-pointer transition-all"
                    style={{
                      backgroundColor: form.tag_ids?.includes(t.id)
                        ? t.color
                        : "transparent",
                      color: form.tag_ids?.includes(t.id) ? "white" : t.color,
                      borderColor: t.color,
                    }}
                    onClick={() => toggleTag(t.id)}
                  >
                    {t.name}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Color Variants */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-primary font-medium">
                  Color Variants
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addColorVariant}
                  className="border-primary text-primary"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Variant
                </Button>
              </div>
              <div className="space-y-4">
                {form.color_variants.map((variant, index) => (
                  <div
                    key={index}
                    className="p-4 border border-[#E7E5E4] rounded-lg bg-canvas/50"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="text-sm font-medium text-primary">
                        Variant {index + 1}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeColorVariant(index)}
                        className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Color
                        </Label>
                        <Select
                          value={variant.color_id}
                          onValueChange={(v) =>
                            updateColorVariant(index, "color_id", v)
                          }
                        >
                          <SelectTrigger className="input-base mt-1 h-9">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {colors.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-4 h-4 rounded-full border"
                                    style={{ backgroundColor: c.hex_code }}
                                  />
                                  {c.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          Stock
                        </Label>
                        <Input
                          type="number"
                          value={variant.stock}
                          onChange={(e) =>
                            updateColorVariant(index, "stock", e.target.value)
                          }
                          className="input-base mt-1 h-9"
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">
                          SKU
                        </Label>
                        <Input
                          value={variant.sku || ""}
                          onChange={(e) =>
                            updateColorVariant(index, "sku", e.target.value)
                          }
                          className="input-base mt-1 h-9"
                          placeholder="SKU-001"
                        />
                      </div>
                    </div>
                    {/* Images upload and preview */}
                    <div className="mt-3">
                      <Label className="text-xs text-muted-foreground mb-1 block">
                        Images
                      </Label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {variant.images?.length > 0 ? (
                          variant.images.map((img, imgIndex) => (
                            <div
                              key={imgIndex}
                              className="relative group shadow-sm rounded-md overflow-hidden border border-gray-200"
                            >
                              <img
                                src={img}
                                alt={`Variant ${index + 1}`}
                                className="w-20 h-20 object-cover rounded-md"
                              />
                              <button
                                type="button"
                                className="absolute -top-1 -right-1 bg-white border border-gray-300 rounded-full p-0.5 shadow hover:bg-red-500 hover:text-white transition-all text-xs"
                                title="Remove image"
                                onClick={() => {
                                  const newImages = variant.images.filter(
                                    (_, i) => i !== imgIndex,
                                  );
                                  updateColorVariant(
                                    index,
                                    "images",
                                    newImages,
                                  );
                                }}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No images
                          </span>
                        )}
                      </div>
                      <div
                        className="relative flex flex-col items-center justify-center border-2 border-dashed border-primary/40 rounded-lg p-4 bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer group"
                        onClick={() =>
                          document
                            .getElementById(`file-upload-${index}`)
                            .click()
                        }
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.add(
                            "ring-2",
                            "ring-primary",
                          );
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.currentTarget.classList.remove(
                            "ring-2",
                            "ring-primary",
                          );
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          const files = Array.from(e.dataTransfer.files);
                          let newImages = [...(variant.images || [])];
                          for (const file of files) {
                            const url = await handleImageUpload(
                              editingProduct ? editingProduct : form,
                              index,
                              file,
                            );
                            if (url) {
                              newImages.push(url);
                            }
                          }
                          updateColorVariant(index, "images", newImages);
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label="Upload images"
                      >
                        <Upload className="w-6 h-6 text-primary mb-1 group-hover:scale-110 transition-transform" />
                        <span className="font-medium text-primary text-sm">
                          Upload Images
                        </span>
                        <span className="text-xs text-muted-foreground mt-1">
                          Drag & drop or click to select
                        </span>
                        <input
                          id={`file-upload-${index}`}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            let newImages = [...(variant.images || [])];
                            for (const file of files) {
                              const url = await handleImageUpload(
                                editingProduct ? editingProduct : form,
                                index,
                                file,
                              );
                              if (url) {
                                newImages.push(url);
                              }
                            }
                            updateColorVariant(index, "images", newImages);
                            e.target.value = null;
                          }}
                        />
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        You can upload multiple images. Click the × on an image
                        to remove it.
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                data-testid="product-active-switch"
              />
              <Label className="text-primary font-medium">
                Product is active
              </Label>
            </div>

            {/* SEO */}
            <div className="space-y-3">
              <Label className="text-primary font-medium">SEO (Optional)</Label>
              <Input
                value={form.meta_title}
                onChange={(e) =>
                  setForm({ ...form, meta_title: e.target.value })
                }
                placeholder="Meta Title"
                className="input-base"
              />
              <Textarea
                value={form.meta_description}
                onChange={(e) =>
                  setForm({ ...form, meta_description: e.target.value })
                }
                placeholder="Meta Description"
                className="input-base"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary text-white hover:bg-primary/90"
              data-testid="save-product-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              {editingProduct ? "Update Product" : "Create Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
